import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { ChartLegend, GroupedBarChart, KpiDelta, KpiHeader, } from '@cmd/ui-components';
import { CacheProvider } from '@emotion/react';
import { Box } from '@interstate/components/Box';
import { InterstateThemeProvider } from '@interstate/components/InterstateThemeProvider';
import { interstateThemeRegistry } from '@interstate/components/InterstateTheming/themes/interstateThemeRegistry';
import { IntlProvider } from 'react-intl';
import { getChartDataFromDaysAgedResponse, getFormattedUnitsSoldData, showNoDataChartMask, } from '../../tiles/tiles/units-sold/unitsSoldUtils';
import { TransactionsResource } from '../../utils/apis/transactionsApiUtils';
import useGetsDataPkce from '../pkce/useGetsDataPkce';
import useWebComponentCache from '../useWebComponentCache';
import WebComponentTile from '../web-component-tile/WebComponentTile';
import WebComponentTileMask from '../web-component-tile/WebComponentTileMask';
import { webComponentDefaultDateRange, DEFAULT_WEB_COMPONENT_TILE_SIZE, getWebComponentDeltas, } from '../webComponentUtils';
const colors = {
    NEW: '#003468',
    USED: '#2372b8',
    CPO: '#81b7e4',
};
const UnitsSoldWebComponent = ({ commonOrgId, partition, }) => {
    const [tileSize, setTileSize] = useState(DEFAULT_WEB_COMPONENT_TILE_SIZE);
    const { cache, stylesLoading } = useWebComponentCache('cmd-ctr-units-sold', partition);
    const { startDate, endDate, rangeType } = webComponentDefaultDateRange();
    const params = {
        orgId: commonOrgId,
        startDate,
        endDate,
    };
    const { data, isLoading, error } = useGetsDataPkce([TransactionsResource.UNITS_SOLD, TransactionsResource.DAYS_AGED].map((resource) => ({
        baseUrl: '/api/pkce/transactions/',
        resource,
    })), params, partition, [
        (res) => (getFormattedUnitsSoldData(res)),
        (res) => ({
            chartData: getChartDataFromDaysAgedResponse(res, colors),
        }),
    ]);
    const { chartData, deltas, lastUpdatedUTC, totals, } = data || {};
    const legendItems = [
        {
            color: colors.NEW,
            label: 'New',
            value: totals?.newCount || 0,
        },
        {
            color: colors.USED,
            label: 'Used',
            value: totals?.usedCount || 0,
        },
        {
            color: colors.CPO,
            label: 'CPO',
            value: totals?.cpoCount || 0,
        },
    ];
    const hasError = error.some((err) => err !== null);
    return cache && (_jsx(React.StrictMode, { children: _jsx(CacheProvider, { value: cache, children: _jsx(InterstateThemeProvider, { applicationName: "Command Center", loadCSSBaseline: false, scopeName: "Command Center Web Components", themeName: "Interstate", themeRegistries: [interstateThemeRegistry], children: _jsx(IntlProvider, { locale: "en", children: _jsxs(WebComponentTile, { dataPeriodLabel: rangeType, error: hasError, isLoading: isLoading, noData: ((totals || {}).totalCount || 0) === 0, onResize: setTileSize, title: "Sold Vehicle Transactions", description: "Confirmed sold inventory and their time on lot", lastUpdated: lastUpdatedUTC ? new Date(lastUpdatedUTC) : undefined, source: "VinSolutions", stylesLoading: stylesLoading, children: [_jsx(KpiHeader, { label: "Units Sold", kpiDeltas: (_jsx(_Fragment, { children: getWebComponentDeltas(deltas).map(({ period, deltaValue }) => (_jsx(KpiDelta, { deltaValue: deltaValue, period: period }, `wc-units-sold-delta-${period}`))) })), size: tileSize === 'xsmall' ? 'large' : tileSize, value: totals?.totalCount || 0 }), _jsx(Box, { sx: {
                                    flex: 1,
                                    display: 'grid',
                                    gridTemplateRows: 'auto 1fr',
                                }, children: tileSize !== 'xsmall' && (_jsxs(_Fragment, { children: [_jsx(Box, { sx: {
                                                margin: `${tileSize === 'small' ? 8 : 16}px 0`,
                                            }, children: _jsx(ChartLegend, { legendItems: legendItems, size: tileSize === 'large' ? 'large' : 'small' }) }), _jsxs(Box, { sx: {
                                                position: 'relative',
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }, children: [!hasError && showNoDataChartMask(chartData, totals) && (_jsx(WebComponentTileMask, { noData: true, size: tileSize })), _jsx(GroupedBarChart, { axisLabel: "Days Aged", data: chartData || [], showTooltip: true, tooltipValueFormatter: (value) => (`${value} Vehicle${value !== 1 ? 's' : ''}`), useSmallTooltip: tileSize !== 'large', vertical: true })] })] })) })] }) }) }) }) }));
};
export default UnitsSoldWebComponent;
